import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
    Header, Image, Button,
    Divider, Label, Message, Loader, Container, Icon
} from 'semantic-ui-react';

const COMMUNITY_GROUP_ID = 2;

const GET_POST_FOR_REVIEW = gql`
     query Post($postId: Int!) {
         post(id: $postId) {
             id
             title
             description
             tags {
              id
              name
              groupId
             }
             type
             publishAt
             poster {
                 name
                 photoUrl
             }
             images {
                 id
                 url
                 tags {
                  id
                  name
                  groupId
                 }
             }
         }
     }
`;

const UPDATE_POST_STATUS = gql`
    mutation UpdatePostStatus($updatePostStatusId: Int!, $status: String!) {
        updatePostStatus(id: $updatePostStatusId, status: $status) {
            id
        }
    }
`;

// --- Component ---

const PostReviewView: React.FC = () => {
    const { postId } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const [isExiting, setIsExiting] = useState(false);

    const { data, loading, error } = useQuery(GET_POST_FOR_REVIEW, {
        variables: { postId: Number(postId) },
        skip: !postId
    });

    const [updateStatus, { loading: updating }] = useMutation(UPDATE_POST_STATUS);

    /**
     * Memoized logic to categorize tags and validate image tagging consistency
     */
    const { communities, postTags, warnings } = useMemo(() => {
        if (!data?.post) return { communities: [], postTags: [], warnings: [] };

        const post = data.post;
        const comms = post.tags.filter((t: any) => t.groupId === COMMUNITY_GROUP_ID);
        const tags = post.tags.filter((t: any) => t.groupId !== COMMUNITY_GROUP_ID);

        const validationWarnings: string[] = [];

        post.images.forEach((img: any, index: number) => {
            const imgGroupIds = new Set(img.tags.map((t: any) => t.groupId as number));

            comms.forEach((c: any) => {
                if (!imgGroupIds.has(c.groupId as number)) {
                    validationWarnings.push(`Image #${index + 1} is not tagged for "${c.name}".`);
                }
            });
        });

        return { communities: comms, postTags: tags, warnings: validationWarnings };
    }, [data]);

    const handleAction = async (status: 'APPROVED' | 'REJECTED') => {
        try {
            await updateStatus({
                variables: {
                    updatePostStatusId: Number(postId),
                    status
                }
            });
            setIsExiting(true);
            setTimeout(() => navigate('/posts/reviews'), 500);
        } catch (err) {
            console.error("Review action failed", err);
        }
    };

    if (loading) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading Post...</Loader>;
    if (error || !data?.post) return <Message error content="Could not load post details." />;

    const { post } = data;

    return (
        <Container
            text
            style={{
                padding: '3em 0em',
                transition: 'opacity 0.5s ease-in-out',
                opacity: isExiting ? 0 : 1
            }}
        >
            <Label basic color="grey" size="small" style={{ marginBottom: '1em' }}>{post.type}</Label>

            {/* Communities Display */}
            {communities.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                    <span style={{ marginRight: '10px', fontWeight: 'bold', color: '#666' }}>Communities:</span>
                    {communities.map((c: any) => (
                        <Label key={c.id} color="purple" size="small" style={{ marginBottom: '5px' }}>
                            <Icon name="users" /> {c.name}
                        </Label>
                    ))}
                </div>
            )}

            <Header as="h1" style={{ fontSize: '2.5em', marginTop: '0' }}>{post.title}</Header>

            {/* Post Standard Tags */}
            {postTags.length > 0 && (
                <div style={{ marginBottom: '1.5em' }}>
                    {postTags.map((t: any) => (
                        <Label key={t.id} basic color="teal" size="tiny" style={{ marginRight: '5px' }}>
                            #{t.name}
                        </Label>
                    ))}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2em', color: '#666' }}>
                <Image avatar src={post.poster.photoUrl} />
                <span style={{ marginLeft: '0.8em' }}>
                    <strong>{post.poster.name}</strong> • {new Date(Number(post.publishAt)).toLocaleString()}
                </span>
            </div>

            {/* Warning Message Box */}
            {warnings.length > 0 && (
                <Message warning icon>
                    <Icon name="warning sign" />
                    <Message.Content>
                        <Message.Header>Tag Mismatch Detected</Message.Header>
                        <ul style={{ marginTop: '5px' }}>
                            {warnings.map((w, i) => (
                                <li key={i}>{w} Are you sure you want to send this post out?</li>
                            ))}
                        </ul>
                    </Message.Content>
                </Message>
            )}

            {/* Image Gallery with Conditional Red Border */}
            {post.images.length > 0 && (
                <div style={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: '12px',
                    paddingBottom: '15px',
                    marginBottom: '2em'
                }}>
                    {post.images.map((img: any, index: number) => {
                        const postGroupIds: number[] = communities.map((c: any) => c.groupId);
                        const imgGroupIds: number[] = img.tags.map((t: any) => t.groupId);

                        // Explicitly typing 'id' as number to fix the implicit any error
                        const isMissingTags = postGroupIds.some((id: number) => !imgGroupIds.includes(id));

                        return (
                            <div key={img.id} style={{ position: 'relative', flex: '0 0 auto' }}>
                                <Image
                                    src={img.url}
                                    rounded
                                    bordered
                                    style={{
                                        height: '220px',
                                        objectFit: 'cover',
                                        border: isMissingTags ? '3px solid #db2828' : '1px solid #ddd'
                                    }}
                                />
                                {isMissingTags && (
                                    <Label color="red" corner="left" icon="warning" />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <p style={{ fontSize: '1.3em', lineHeight: '1.6', whiteSpace: 'pre-wrap', color: '#333' }}>
                {post.description}
            </p>

            <Divider style={{ margin: '4em 0 2em 0' }} />

            <div style={{ display: 'flex', gap: '20px' }}>
                <Button
                    basic
                    color="red"
                    size="huge"
                    fluid
                    icon="trash"
                    labelPosition="left"
                    content="Delete"
                    loading={updating}
                    disabled={isExiting}
                    onClick={() => handleAction('REJECTED')}
                />
                <Button
                    positive
                    size="huge"
                    fluid
                    icon="checkmark"
                    labelPosition="left"
                    content={warnings.length > 0 ? "Approve Anyway" : "Approve"}
                    loading={updating}
                    disabled={isExiting}
                    onClick={() => handleAction('APPROVED')}
                />
            </div>
        </Container>
    );
};

export { PostReviewView };
